import React, { useState, useEffect, useCallback } from "react"
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core"
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable"
import SortableModalItem from "./SortableModalItem"

interface TableSortingModalProps {
  isOpen: boolean
  onClose: () => void
  tableOrder: string[]
  setTableOrder: (order: string[]) => void
  tableConfigs: Record<string, { title: string; available?: boolean }>
  defaultTableOrder: string[]
}

const TableSortingModal: React.FC<TableSortingModalProps> = ({
  isOpen,
  onClose,
  tableOrder,
  setTableOrder,
  tableConfigs,
  defaultTableOrder,
}) => {
  const [localOrder, setLocalOrder] = useState(tableOrder)

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event

    if (over && active.id !== over.id) {
      setLocalOrder(items => {
        const oldIndex = items.indexOf(active.id as string)
        const newIndex = items.indexOf(over.id as string)
        return arrayMove(items, oldIndex, newIndex)
      })
    }
  }, [])

  const handleSave = () => {
    setTableOrder(localOrder)
    onClose()
  }

  const handleCancel = () => {
    setLocalOrder(tableOrder)
    onClose()
  }

  const handleReset = () => {
    setLocalOrder(defaultTableOrder)
  }

  useEffect(() => {
    setLocalOrder(tableOrder)
  }, [tableOrder])

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-system-blue-900 border border-system-blue-700 rounded-system-lg shadow-2xl w-full max-w-lg mx-4">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-white">Reorder Tables</h2>
            <button
              onClick={onClose}
              className="text-system-blue-300 hover:text-white transition-colors"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          <div className="mb-6">
            <p className="text-system-blue-200 text-sm mb-4">
              Drag and drop to reorder the KPI tables according to your
              preference.
            </p>

            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={localOrder}
                strategy={verticalListSortingStrategy}
              >
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {localOrder.map(tableKey => {
                    const config =
                      tableConfigs[tableKey as keyof typeof tableConfigs]
                    if (!config) return null
                    if (config.available === false) return null

                    return (
                      <SortableModalItem
                        key={tableKey}
                        id={tableKey}
                        title={config.title}
                      />
                    )
                  })}
                </div>
              </SortableContext>
            </DndContext>
          </div>

          <div className="flex items-center justify-between">
            <button
              onClick={handleReset}
              className="px-4 py-2 text-sm text-system-blue-300 hover:text-white border border-system-blue-600 hover:border-system-blue-500 rounded-system-md transition-colors"
            >
              Reset to Default
            </button>

            <div className="flex space-x-3">
              <button
                onClick={handleCancel}
                className="px-4 py-2 text-sm text-system-blue-300 hover:text-white border border-system-blue-600 hover:border-system-blue-500 rounded-system-md transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="px-4 py-2 text-sm text-white bg-system-brand-pink-500 hover:bg-system-brand-pink-600 rounded-system-md transition-colors"
              >
                Save Order
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default TableSortingModal
